import { ClickstreamActivityData } from './../../types/clickstream.interface';
import { Injectable, ProviderToken, inject } from '@angular/core';
import { ClickstreamEventData } from '../../types';
import { ClickstreamAPI } from '../../classes/clickstream-api/clickstream-api';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { MessageService } from 'src/app/modules/message/services/message/message.service';
import { MessageCodes } from 'src/app/shared/enums';
import { FeatureFlagService } from 'src/app/modules/feature-flags/services/feature-flag/feature-flag.service';
import { FeatureFlags } from 'src/app/shared/enums/feature-flags';
import { ClickstreamEventTypeName } from '../../enums/clickstream-events';
import { ClickstreamInteraction } from '../../types/clickstream-interaction.interface';
import { CookieService } from 'ngx-cookie-service';

const getClickstreamFacade = (): ClickstreamAPI => {
  const injectionPoint: ProviderToken<ClickstreamAPI> = ClickstreamAPI;
  const facade = inject(injectionPoint);
  return facade;
};

@Injectable({
  providedIn: 'root',
})
export class ClickstreamService {
  public clickstream = getClickstreamFacade();
  private cookieKey = 'clickstream.session';

  private clickstreamInteractions: ClickstreamInteraction[] = [];
  private preventClickstream = false;

  constructor(
    private oidcSecurityService: OidcSecurityService,
    private messageService: MessageService,
    private featureFlagService: FeatureFlagService,
    private cookieService: CookieService
  ) {
    this.preventClickstream = !this.featureFlagService.isFlagEnabled(
      FeatureFlags.USE_CLICKSTREAM
    );
    this.featureFlagService.flagsChanged.subscribe(() => {
      this.preventClickstream = !this.featureFlagService.isFlagEnabled(
        FeatureFlags.USE_CLICKSTREAM
      );
    });
  }

  getStartTime(): number {
    return this.clickstream.getStartTime();
  }

  establishSession(): void {
    if (!this.clickstream.sessionId) {
      this.clickstream.sessionId = this.getSessionIdFromCookie();
    }
  }

  setSessionId(sessionId?: string): void {
    if (sessionId) {
      this.clickstream.sessionId = sessionId;
    } else {
      this.clickstream.sessionId = crypto.randomUUID();
    }
  }

  getSessionId(): string {
    return this.clickstream.sessionId;
  }

  getSessionIdFromCookie(): string {
    const cookieValue = this.cookieService.check(this.cookieKey)
      ? this.cookieService.get(this.cookieKey)
      : crypto.randomUUID();

    return cookieValue;
  }

  async checkForAccessToken(): Promise<boolean> {
    if (this.preventClickstream) {
      return false;
    }
    if (this.clickstream.authToken) {
      return true;
    }
    this.oidcSecurityService.getAccessToken().subscribe({
      next: token => {
        if (token) {
          this.clickstream.setAuthToken(token);
        }
      },
      error: err => {
        console.error('we had issues getting an access token', err);
        this.messageService.showMessage(MessageCodes.HTTP_AUTH_REQUIRED);
      },
    });
    return true;
  }

  setSupervisorContext(subjectName: string): void {
    if (this.preventClickstream) {
      return;
    }

    this.clickstream.setSupervisorContext(subjectName);
  }

  async startSession(activityName: string): Promise<void> {
    if (this.preventClickstream) {
      return;
    }
    await this.checkForAccessToken();

    this.clickstream.startSession(activityName);
  }

  startActivity(
    activityUuid: string,
    contentOid: number,
    activityName: string,
    category?: string,
    scoreCriteria?: string,
    difficulty?: number
  ): void {
    if (this.preventClickstream) {
      return;
    }
    const activityData: ClickstreamActivityData = {
      activityUuid: activityUuid,
      contentOid: contentOid,
      activityName: activityName,
      category: category ?? 'n/a',
      scoreCriteria: scoreCriteria ?? 'n/a',
      difficulty: difficulty ?? 0,
    };

    this.clickstream.initializeActivity(activityData, true);
  }

  continueAmiraActivity(inboxActivityId: string): void {
    if (this.preventClickstream) {
      return;
    }
    this.clickstream.continueAmiraActivity(inboxActivityId);
  }

  completeActivity(score = 0, numStars = 0, offset: number): void {
    if (this.preventClickstream) {
      return;
    }
    this.clickstream.completeActivity(score, numStars, offset);
  }

  completeAmiraActivity(score = 0, offset = 0, amiraActivityId = ''): void {
    if (this.preventClickstream) {
      return;
    }
    this.clickstream.completeAmiraActivity(score, offset, amiraActivityId);
  }

  interruptActivity(): void {
    if (this.preventClickstream) {
      return;
    }
    this.addEventFromProperties(
      ClickstreamEventTypeName.ActivityInterrupt,
      'Exit',
      'Exit Button'
    );
  }

  addEventFromProperties(
    eventType: string,
    key: string,
    clientData?: unknown,
    offset?: number
  ): void {
    if (this.preventClickstream) {
      return;
    }
    const event = {
      eventType: eventType,
      key: key,
      offset: offset ?? 0,
      data: clientData,
    };
    this.clickstream.addEvent(event);
  }

  addEvent(event: ClickstreamEventData): void {
    if (this.preventClickstream) {
      return;
    }
    this.clickstream.addEvent(event);
  }

  saveClickstreamData(): void {
    if (this.preventClickstream) {
      return;
    }
    this.clickstream.saveClickstreamData();
  }

  clearCache(): void {
    if (this.preventClickstream) {
      return;
    }
    this.clickstream.clearCache();
  }

  clearEvents(): void {
    if (this.preventClickstream) {
      return;
    }
    this.clickstream.clearEvents();
  }

  setClickstreamInteractions(
    clickstreamInteractions: ClickstreamInteraction[]
  ): void {
    this.clickstreamInteractions = clickstreamInteractions;
  }

  getClickstreamInteractions(): ClickstreamInteraction[] {
    return this.clickstreamInteractions;
  }

  recordClickstreamInteraction(
    type: number,
    offset: number,
    manifestId: string,
    data?: unknown
  ): void {
    if (!this.clickstreamInteractions) {
      this.clickstreamInteractions = [];
    }
    this.clickstreamInteractions.push({
      type: type,
      offset: offset,
      data: data,
      manifestId: manifestId,
    });
  }

  clearClickstreamInteractionLog(): void {
    this.clickstreamInteractions = [];
  }
}
